import { useI18n } from '@ecom/i18n';
import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useOverlay } from '../utils/use-overlay';

import { CloseIcon } from './CloseIcon';

export interface ModalProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly onClose: () => void;
  /** Rendered in the footer, typically the confirm/cancel `Button` pair. */
  readonly footer?: ReactNode;
  /** `lg` for a form in two columns (it stacks to one on a phone). Default `md`. */
  readonly size?: 'md' | 'lg';
  readonly children: ReactNode;
}

/**
 * Centred modal dialog (§7.1 primitives).
 *
 * No modal exists in the three prototypes — the only overlay they ship is the
 * cart drawer — so the surface treatment is assembled from tokens already in
 * use: the `Card` surface and radius, and the drawer's own scrim colour
 * (`rgba(10,16,14,.45)`, taken verbatim from `#cartOverlay`). Nothing new was
 * introduced to the palette.
 *
 * Rendered through a portal on `document.body` so the dialog is never clipped
 * by an ancestor's `overflow` or trapped beneath one via `z-index` stacking
 * context — both are routine causes of a dialog that appears "behind" the
 * page.
 */
export function Modal({
  open,
  title,
  description,
  onClose,
  footer,
  size = 'md',
  children,
}: ModalProps): ReactNode {
  const { t } = useI18n();
  const id = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  useOverlay({ open, containerRef, onClose });

  if (!open) return null;

  const titleId = `${id}-title`;
  const descriptionId = description !== undefined ? `${id}-description` : undefined;

  return createPortal(
    <div className="ecom-overlay">
      {/* Scrim. Presentational: closing by clicking outside is also available
          via Escape, so this carries no role and is hidden from assistive
          technology rather than announced as an unlabelled clickable region. */}
      <div className="ecom-overlay__scrim" onClick={onClose} aria-hidden="true" />

      <div
        ref={containerRef}
        className="ecom-modal"
        data-size={size}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(descriptionId !== undefined ? { 'aria-describedby': descriptionId } : {})}
        tabIndex={-1}
      >
        <header className="ecom-modal__header">
          <div>
            <h2 id={titleId} className="ecom-modal__title">
              {title}
            </h2>
            {description !== undefined ? (
              <p id={descriptionId} className="ecom-modal__description">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="ecom-overlay__close"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </header>

        <div className="ecom-modal__body">{children}</div>

        {footer !== undefined ? <footer className="ecom-modal__footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
